import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogActions, DialogContent, Grid, Typography } from "@mui/material";
import { MprcButton, MprcClickable, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonDelete, ButtonSave } from "components/buttons";
import { CreateOrUpdateShortVideoCommand, FeedAttachDto, ShortVideoDto, ShortVideoSettingsDto } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NIL } from "uuid";
import { EditShortVideo } from "views/knowledge/shorts/Home/EditShorts/EditShortVideo";

////////////////////////////////////////////////////////////////////////////////

interface IShortVideoDialogProps {
    shortVideoId?: string | null;
    publicationId: string;
    attachId?: string | null;
    title: string;
    allowDelete: boolean;
    onClose: (reload?: boolean) => void;
}

function getInitialSettings(record: ShortVideoDto) {
    return {
        startTime: 0,
        endTime: Math.min(120, record.attach.fileDuration ?? record.attach.linkDuration ?? 0),
        priorityView: false,
        dueDate: null
    };
}

export const EditShortVideoDialog = (props: IShortVideoDialogProps) => {
    const { shortVideoId, publicationId, attachId, title, allowDelete, onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [videos, setVideos] = useState<ShortVideoDto[]>([]);
    const [changed, setChanged] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [currentIndex, setCurrentIndex] = useState<number>(-1);
    const [viewError, setViewError] = useState<any>();
    const [record, setRecord] = useState<ShortVideoSettingsDto | null>(null);
    const [loadDto, setLoadDto] = useState<ShortVideoSettingsDto | null>(null);
    const [currentAttach, setCurrentAttach] = useState<FeedAttachDto>(null!);

    ///
    const load = async () => {
        try {

            if (shortVideoId == null) {
                const result = await api.v1.knowledgePublication.getPublicationShortVideos({ publicationId, attachId: attachId ?? null });
                setVideos(result.data);
            }
            else {
                const result = await api.v1.knowledgeShortVideo.getShortVideo({ shortVideoId: shortVideoId! });
                setVideos([result.data]);
                setRecord(result.data.settings);
            }

            setCurrentIndex(0);
        }
        catch (error: any) {
            setViewError(error);
        }
        finally {
            setLoading(false);
        }
    };

    ///
    const handleChange = (props: Partial<ShortVideoSettingsDto>) => {
        setRecord(state => ({ ...state!, ...props }));
    };

    ///
    const handleReadyToSave = (valid: boolean) => {
        setIsValid(valid);
    };

    const initEditRecord = (index: number) => {
        let dto = videos[index].settings;
        setLoadDto(LibUtils.deepCopy(dto));
        setRecord(dto ?? getInitialSettings(videos[index]));
    };

    ///
    const handleRestore = () => {
        setCurrentAttach(LibUtils.deepCopy(videos[currentIndex].attach)); // Força a reinicialização do EditShortVideo.

        // Reinicializa Settings
        const rdto = LibUtils.deepCopy(loadDto);
        setRecord(rdto ?? getInitialSettings(videos[currentIndex]));
    };

    ///
    const handleCancel = () => {
        onClose(changed);
    };

    ///
    const handleSave = async () => {
        const newVideo: ShortVideoDto = { ...videos[currentIndex], settings: record };

        try {
            setSaving(true);

            var dto: CreateOrUpdateShortVideoCommand = {
                shortVideoId: newVideo.shortVideoId,
                attachId: newVideo.attach.attachId,
                settings: newVideo.settings!
            };

            const result = await api.v1.knowledgeShortVideo.saveShortVideo(dto);
            if (result.data != null) {
                if (videos.length == 1) {
                    onClose(true);
                }
                else {
                    setChanged(true);
                    videos[currentIndex] = { ...newVideo, shortVideoId: result.data };
                    setRecord(newVideo.settings);
                    setLoadDto(LibUtils.deepCopy(newVideo.settings));
                }
            }
        }
        catch (error: any) {
            setViewError(error);
        }
        finally {
            setSaving(false);
        }
    };

    ///
    const handleDelete = async () => {
        try {
            setSaving(true);

            const result = await api.v1.knowledgeShortVideo.deleteShortVideo({ shortVideoId: videos[currentIndex].shortVideoId });
            if (result.data === true) {
                enqueueSnackbar(t("ShortVideo.EditDialog.DeleteSuccessMessage"), { variant: "success" });
                if (videos.length == 1) {
                    onClose(true);
                }
                else {
                    videos[currentIndex].shortVideoId = NIL;
                    videos[currentIndex].settings = null;

                    setCurrentAttach(LibUtils.deepCopy(videos[currentIndex].attach)); // Força a reinicialização do EditShortVideo.
                    const rdto = null;
                    setLoadDto(rdto);
                    setRecord(rdto);
                    setChanged(true);
                }
            }
            else enqueueSnackbar(t("ShortVideo.EditDialog.DeleteErrorMessage"), { variant: "error" });

        } catch {
            enqueueSnackbar(t("ShortVideo.EditDialog.DeleteErrorMessage"), { variant: "error" });
        }
        finally {
            setSaving(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        load();
    }, []);

    ///
    useEffect(() => {
        if (currentIndex > -1) {
            setCurrentAttach(videos[currentIndex].attach);
            if (videos.length == 1) {
                initEditRecord(currentIndex);
            }
            else if (videos.length > 1) {
                const dto = videos[currentIndex].settings;
                setLoadDto(LibUtils.deepCopy(dto));
                setRecord(dto);
            }
        }
    }, [currentIndex]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const isRecordChanged = !LibUtils.deepCompare(record, loadDto);
    const canSave = isRecordChanged && isValid;

    return (
        <Dialog
            open
        >
            <MprcDialogTitleClose
                title={t("ShortVideo.EditDialog.Title")}
                subtitle={title}
                onClose={() => onClose(changed)}
                shrinkPadding
            />

            <DialogContent sx={{ minWidth: 700 }}>

                <MprcRequestIndicator visible={saving} type="post" />
                <MprcRequestIndicator visible={loading} type="request" />

                {
                    viewError && videos.length == 0
                        ? <MprcRequestErrorReport error={viewError} />
                        : (
                            <>
                                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

                                <Typography variant="overline" component="div">
                                    {t("ShortVideo.EditDialog.Message1")}
                                </Typography>

                                <Typography variant="caption">
                                    {t("ShortVideo.EditDialog.Message2")}
                                </Typography>

                                {
                                    videos.length > 1 &&
                                    <Grid container>
                                        <Grid size="grow">
                                            <Typography variant="caption">
                                                {t("ShortVideo.EditDialog.Message3", { count: videos.length })}
                                            </Typography>
                                        </Grid>
                                        <Grid>
                                            <Typography variant="caption">
                                                <MprcClickable onClick={() => setCurrentIndex(state => state - 1)} disabled={isRecordChanged || currentIndex == 0}>
                                                    <FontAwesomeIcon icon={faChevronLeft} fixedWidth></FontAwesomeIcon>
                                                </MprcClickable>
                                                <span>{currentIndex + 1} / {videos.length}</span>
                                                <MprcClickable onClick={() => setCurrentIndex(state => state + 1)} disabled={isRecordChanged || currentIndex == videos.length - 1}>
                                                    <FontAwesomeIcon icon={faChevronRight} fixedWidth></FontAwesomeIcon>
                                                </MprcClickable>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                }

                                {currentAttach != null &&
                                    <EditShortVideo
                                        attach={currentAttach}
                                        record={record}
                                        onChange={handleChange}
                                        onReadyToSave={handleReadyToSave}
                                    />
                                }
                            </>
                        )
                }
            </DialogContent>
            <DialogActions>
                <Grid container justifyContent="space-between" alignItems="center" width="100%">
                    <Grid marginLeft={1}>
                        {
                            allowDelete && currentIndex > -1 && videos[currentIndex].settings != null &&
                            <>
                                <ButtonDelete variant="contained" onClick={handleDelete} />
                                &nbsp;&nbsp;
                            </>
                        }
                        {
                            record != null &&
                            <MprcButton
                                text={t("ShortVideo.EditDialog.RestoreButtonText")}
                                title={t("ShortVideo.EditDialog.RestoreButtonTitle")}
                                variant="contained"
                                color="cancel"
                                disabled={!isRecordChanged}
                                onClick={handleRestore}
                            />
                        }
                    </Grid>

                    <Grid marginRight={1}>
                        {
                            videos.length > 1 && record == null
                                ? <MprcButton
                                    text={t("ShortVideo.EditDialog.CreateButtonText")}
                                    title={t("ShortVideo.EditDialog.CreateButtonTitle")}
                                    variant="contained"
                                    onClick={handleRestore}
                                />
                                : <>
                                    {
                                        videos.length == 1 &&
                                        <>
                                            <ButtonCancel variant="contained" onClick={handleCancel} />
                                            &nbsp;&nbsp;
                                        </>

                                    }
                                    <ButtonSave disabled={!canSave} onClick={handleSave} />
                                </>
                        }

                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog >
    );
};
